import { PromotionType } from '@/__generated__/types';

// ----------------------------------------------------------------------
function path(root: string, subLink: string): string {
    return `${root}${subLink}`;
}

const ROOTS_AUTH = '/auth';
const ROOTS_DASHBOARD = '/dashboard';
const ROOTS_PUBLIC = '/public';

// ----------------------------------------------------------------------

export const PATH_AUTH = {
    root: ROOTS_AUTH,
    login: path(ROOTS_AUTH, '/login'),
    loginUnprotected: path(ROOTS_AUTH, '/login-unprotected'),
    register: path(ROOTS_AUTH, '/register'),
    registerUnprotected: path(ROOTS_AUTH, '/register-unprotected'),
    resetPassword: path(ROOTS_AUTH, '/reset-password'),
    verify: path(ROOTS_AUTH, '/verify')
};

export const PATH_PAGE = {
    comingSoon: '/coming-soon',
    maintenance: '/maintenance',
    pricing: '/pricing',
    payment: '/payment',
    about: '/about-us',
    contact: '/contact-us',
    faqs: '/faqs',
    page404: '/404',
    page500: '/500',
    components: '/components'
};

interface generatePromotionPathProps {
    id?: string;
    type?: PromotionType;
    isLoyalty?: boolean;
}

export const PATH_DASHBOARD = {
    root: ROOTS_DASHBOARD,
    public: {
        saveBooking: (merchantId: string, yearStr?: string, bookingNumber?: string): string => {
            const queryParams = [];
            if (yearStr) {
                queryParams.push(`yearStr=${yearStr}`);
            }
            if (bookingNumber) {
                queryParams.push(`bookingNumber=${bookingNumber}`);
            }
            const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
            return path(ROOTS_PUBLIC, `/booking/${merchantId}/save${queryString}`);
        },

        queryBooking: (merchantId: string, yearStr: string, bookingNumber: string): string =>
            path(ROOTS_PUBLIC, `/booking/${merchantId}/query?yearStr=${yearStr}&bookingNumber=${bookingNumber}`),

        bookingTerms: (merchantId: string): string => path(ROOTS_PUBLIC, `/booking/${merchantId}/terms`),
        bookingPrivacy: (merchantId: string): string => path(ROOTS_PUBLIC, `/booking/${merchantId}/privacy`),
        merchantGiftCardBalance: (merchantId: string): string => {
            return path(ROOTS_PUBLIC, `/merchantGiftCard/${merchantId}/balance`);
        },
        posInquiry: (): string => path(ROOTS_PUBLIC, '/pos/inquiry'),
        bookingOnboarding: (): string => path(ROOTS_PUBLIC, '/booking/onboarding'),
        bookingCalendar: (merchantId: string, yearStr: string, bookingNumber: string, _bookingJWTToken: string): string =>
            path(
                ROOTS_PUBLIC,
                `/booking/${merchantId}/calendar?yearStr=${encodeURIComponent(yearStr)}&bookingNumber=${encodeURIComponent(bookingNumber)}&token=${encodeURIComponent(_bookingJWTToken)}`
            )
    },
    general: {
        generalApp: path(ROOTS_DASHBOARD, '/generalApp')
        // ecommerce: path(ROOTS_DASHBOARD, '/ecommerce'),
        // analytics: path(ROOTS_DASHBOARD, '/analytics'),
        // banking: path(ROOTS_DASHBOARD, '/banking')
    },
    mail: {
        root: path(ROOTS_DASHBOARD, '/mail'),
        all: path(ROOTS_DASHBOARD, '/mail/all')
    },
    chat: {
        root: path(ROOTS_DASHBOARD, '/chat'),
        new: path(ROOTS_DASHBOARD, '/chat/new'),
        conversation: path(ROOTS_DASHBOARD, '/chat/:conversationKey')
    },
    calendar: path(ROOTS_DASHBOARD, '/calendar'),
    kanban: path(ROOTS_DASHBOARD, '/kanban'),
    user: {
        root: path(ROOTS_DASHBOARD, '/user'),
        list: path(ROOTS_DASHBOARD, '/user/list'),
        newUser: path(ROOTS_DASHBOARD, '/user/new'),
        editUser: (userId: string): string => path(ROOTS_DASHBOARD, `/user/edit/${userId}`),
        account: path(ROOTS_DASHBOARD, '/user/account')
    },
    system: {
        apiHealth: path(ROOTS_DASHBOARD, '/system/apiHealth'),
        shortUrl: path(ROOTS_DASHBOARD, '/system/shortUrl')
    },
    promotion: {
        root: path(ROOTS_DASHBOARD, '/promotion'),
        list: path(ROOTS_DASHBOARD, '/promotion/list'),
        appQRCode: path(ROOTS_DASHBOARD, '/promotion/appQRCode'),
        history: path(ROOTS_DASHBOARD, '/promotion/history'),
        newOffer: path(ROOTS_DASHBOARD, `/promotion/offer`),
        newPromotion: path(ROOTS_DASHBOARD, '/promotion/new'),
        sendPromotion: path(ROOTS_DASHBOARD, '/promotion/send'),
        sendHistory: path(ROOTS_DASHBOARD, '/promotion/send-history'),
        createPromotionPath: (props: generatePromotionPathProps): string => {
            const { id, type, isLoyalty } = props;
            let subLink = '/promotion';
            if (isLoyalty) {
                subLink += '/loyalty';
            }
            if (type) {
                subLink += `/${type}`;
            }
            if (id) {
                subLink += `/${id}`;
            }
            return path(ROOTS_DASHBOARD, subLink);
        },
        editOffer: (id: string, type: string): string => `${path(ROOTS_DASHBOARD, `/promotion/offer/${id}`)}?type=${type}`
    },
    loyalty: {
        root: path(ROOTS_DASHBOARD, '/loyalty'),
        member: path(ROOTS_DASHBOARD, '/loyalty/member'),
        memberDetail: path(ROOTS_DASHBOARD, '/loyalty/memberDetail')
    },
    membership: {
        root: path(ROOTS_DASHBOARD, '/membership'),
        members: path(ROOTS_DASHBOARD, '/membership/members'),
        memberDetail: path(ROOTS_DASHBOARD, '/membership/memberDetail'),
        plans: path(ROOTS_DASHBOARD, '/membership/plans')
    },
    merchantGiftCard: {
        root: path(ROOTS_DASHBOARD, '/merchantGiftCard'),
        wallets: path(ROOTS_DASHBOARD, '/merchantGiftCard/wallets'),
        member: path(ROOTS_DASHBOARD, '/merchantGiftCard/member'),
        createGiftCard: path(ROOTS_DASHBOARD, '/merchantGiftCard/create'),
        memberDetail: path(ROOTS_DASHBOARD, '/merchantGiftCard/memberDetail'),
        topUp: (userId: string): string => path(ROOTS_DASHBOARD, `/merchantGiftCard/topUp?userId=${userId}`)
    },
    giftCard: {
        root: path(ROOTS_DASHBOARD, '/giftCard'),
        balance: path(ROOTS_DASHBOARD, '/giftCard/balance'),
        accounts: path(ROOTS_DASHBOARD, '/giftCard/accounts'),
        history: path(ROOTS_DASHBOARD, '/giftCard/history'),
        editGiftCardAccount: (id: string): string => path(ROOTS_DASHBOARD, `/giftCard/balance/${id}`),
        viewGiftCardAccountHistory: (id: string): string => path(ROOTS_DASHBOARD, `/giftCard/history/${id}`)
    },
    FAQ: {
        root: path(ROOTS_DASHBOARD, '/FAQ'),
        sections: path(ROOTS_DASHBOARD, '/FAQ/sections'),
        preview: path(ROOTS_DASHBOARD, '/FAQ/preview')
    },
    appSections: {
        root: path(ROOTS_DASHBOARD, '/appSections'),
        list: path(ROOTS_DASHBOARD, '/appSections/list')
    },
    popularRestaurantCategories: {
        root: path(ROOTS_DASHBOARD, '/popular-restaurant-categories'),
        list: path(ROOTS_DASHBOARD, '/popular-restaurant-categories/list')
    },
    forms: {
        root: path(ROOTS_DASHBOARD, '/forms'),
        pos: path(ROOTS_DASHBOARD, '/forms/pos'),
        booking: path(ROOTS_DASHBOARD, '/forms/booking'),
        list: path(ROOTS_DASHBOARD, '/forms/list')
    },
    ticket: {
        root: path(ROOTS_DASHBOARD, '/ticket'),
        create: path(ROOTS_DASHBOARD, '/ticket/create'),
        view: path(ROOTS_DASHBOARD, '/ticket/view')
    },
    merchant: {
        root: path(ROOTS_DASHBOARD, '/merchant'),
        list: path(ROOTS_DASHBOARD, '/merchant/list'),
        uploadPhoto: path(ROOTS_DASHBOARD, '/merchant/upload-photo'),
        newMerchant: path(ROOTS_DASHBOARD, '/merchant/new'),
        updateMerchant: path(ROOTS_DASHBOARD, '/merchant/update-merchant'),
        merchantSurcharges: path(ROOTS_DASHBOARD, '/merchant/surcharges'),
        newMerchantSurcharge: path(ROOTS_DASHBOARD, '/merchant/new-surcharges'),
        updateOpeningHours: path(ROOTS_DASHBOARD, '/merchant/updateOpeningHours'),
        editById: (id: string): string => path(ROOTS_DASHBOARD, `/merchant/${id}/edit`),
        merchantSettings: (id: string): string => path(ROOTS_DASHBOARD, `/merchant/${id}/settings`),
        subscriptions: path(ROOTS_DASHBOARD, `/merchant/subscriptions`),
        productList: path(ROOTS_DASHBOARD, `/merchant/products`)
    },
    menu: {
        root: path(ROOTS_DASHBOARD, '/menu'),
        list: path(ROOTS_DASHBOARD, '/menu/list'),
        newMenu: path(ROOTS_DASHBOARD, '/menu/new'),
        editById: (id: string): string => path(ROOTS_DASHBOARD, `/menu/${id}/edit`),
        menuCategoryByMenuId: (id: string): string => path(ROOTS_DASHBOARD, `/menu/${id}/menuCategory`),
        menuGroup: path(ROOTS_DASHBOARD, '/menu/menuGroup'),
        createMenuGroup: path(ROOTS_DASHBOARD, '/menu/createMenuGroup'),
        cookingCategoryByMenuId: (id: string): string => path(ROOTS_DASHBOARD, `/menu/${id}/cookingCategory`)
    },
    order: {
        root: path(ROOTS_DASHBOARD, '/order'),
        list: path(ROOTS_DASHBOARD, '/order/list'),
        detail: (orderNumber: string): string => path(ROOTS_DASHBOARD, `/order/${orderNumber}/detail`)
    },
    bookingSessions: {
        root: path(ROOTS_DASHBOARD, '/sessions-booking'),
        list: path(ROOTS_DASHBOARD, '/sessions-booking/list'),
        newBooking: path(ROOTS_DASHBOARD, '/sessions-booking/new'),
        editById: (id: string): string => path(ROOTS_DASHBOARD, `/sessions-booking/${id}/edit`)
    },
    managers: {
        root: path(ROOTS_DASHBOARD, '/managers'),
        list: path(ROOTS_DASHBOARD, '/managers/list'),
        create: path(ROOTS_DASHBOARD, '/managers/create')
    },
    booking: {
        root: path(ROOTS_DASHBOARD, '/booking'),
        settings: path(ROOTS_DASHBOARD, '/booking/settings'),
        message: path(ROOTS_DASHBOARD, '/booking/message'),
        google: path(ROOTS_DASHBOARD, '/booking/google'),
        floormap: path(ROOTS_DASHBOARD, '/booking/floormap'),
        updateBookingHours: path(ROOTS_DASHBOARD, '/booking/updateBookingHours'),
        list: (timestamp?: number): string => {
            const basePath = path(ROOTS_DASHBOARD, '/booking/list');
            return timestamp ? `${basePath}?timestamp=${timestamp}` : basePath;
        },
        view: (timestamp?: number): string => {
            const basePath = path(ROOTS_DASHBOARD, '/booking/view');
            return timestamp ? `${basePath}?timestamp=${timestamp}` : basePath;
        },
        calendar: path(ROOTS_DASHBOARD, '/booking/calendar'),
        tables: path(ROOTS_DASHBOARD, '/booking/tables'),
        report: path(ROOTS_DASHBOARD, '/booking/report'),
        platformReport: path(ROOTS_DASHBOARD, '/booking/platformReport'),
        smsReport: path(ROOTS_DASHBOARD, '/booking/smsReport'),
        clients: path(ROOTS_DASHBOARD, '/booking/clients'),
        createBooking: path(ROOTS_DASHBOARD, '/booking/create'),
        link: path(ROOTS_DASHBOARD, '/booking/link'),
        detail: (id: string): string => path(ROOTS_DASHBOARD, `/booking/${id}/detail`)
    },
    announcementBroadcast: {
        root: path(ROOTS_DASHBOARD, '/announcementBroadcast'),
        list: path(ROOTS_DASHBOARD, '/announcementBroadcast/list'),
        create: path(ROOTS_DASHBOARD, '/announcementBroadcast/create'),
        editById: (id: string): string => path(ROOTS_DASHBOARD, `/announcementBroadcast/${id}/edit`)
    },
    bookingSubscription: {
        root: path(ROOTS_DASHBOARD, '/bookingSubscription'),
        // Admin: manage subscription packages
        packages: path(ROOTS_DASHBOARD, '/bookingSubscription/packages'),
        createPackage: path(ROOTS_DASHBOARD, '/bookingSubscription/packages/create'),
        editPackage: (id: string): string => path(ROOTS_DASHBOARD, `/bookingSubscription/packages/${id}/edit`),
        // Admin: manage merchant subscriptions
        merchantSubscriptions: path(ROOTS_DASHBOARD, '/bookingSubscription/merchants'),
        adjustMerchantSubscription: (merchantId: string): string =>
            path(ROOTS_DASHBOARD, `/bookingSubscription/merchants/${merchantId}/adjust`),
        // Merchant: view own subscription
        mySubscription: path(ROOTS_DASHBOARD, '/bookingSubscription/mySubscription')
    },
    eCommerce: {
        root: path(ROOTS_DASHBOARD, '/e-commerce'),
        shop: path(ROOTS_DASHBOARD, '/e-commerce/shop'),
        product: path(ROOTS_DASHBOARD, '/e-commerce/product/:name'),
        productById: path(ROOTS_DASHBOARD, '/e-commerce/product/nike-air-force-1-ndestrukt'),
        list: path(ROOTS_DASHBOARD, '/e-commerce/list'),
        newProduct: path(ROOTS_DASHBOARD, '/e-commerce/product/new'),
        editById: path(ROOTS_DASHBOARD, '/e-commerce/product/nike-blazer-low-77-vintage/edit'),
        checkout: path(ROOTS_DASHBOARD, '/e-commerce/checkout'),
        invoice: path(ROOTS_DASHBOARD, '/e-commerce/invoice')
    },
    blog: {
        root: path(ROOTS_DASHBOARD, '/blog'),
        posts: path(ROOTS_DASHBOARD, '/blog/posts'),
        post: path(ROOTS_DASHBOARD, '/blog/post/:title'),
        postById: path(ROOTS_DASHBOARD, '/blog/post/apply-these-7-secret-techniques-to-improve-event'),
        newPost: path(ROOTS_DASHBOARD, '/blog/new-post')
    },
    transactions: {
        root: path(ROOTS_DASHBOARD, '/transactions')
    },
    reports: {
        root: path(ROOTS_DASHBOARD, '/reports')
    },
    settings: {
        root: path(ROOTS_DASHBOARD, '/settings')
    },
    paymentSubscriptions: {
        success: (id: string): string => `/subscriptions/${id}/payment/success`,
        error: (id: string): string => `/subscriptions/${id}/payment/error`
    }
};

export const PATH_DOCS = 'https://docs-minimals.vercel.app/introduction';
